// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImagePresentationProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel, { SwiperStyle } from "@tps/App/Components/XngCarousel";
import { generateSlidesPerViewMap, isTabletScreen } from "@tps/XDL/Utilities";

import {
    FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE,
    SLIDES_PER_VIEW_POINTS,
    XngCustomGroup,
} from "@tps/XDL/constants";
import React from "react";
import { NavigationOptions, PaginationOptions } from "swiper/types";

const enum fields {
    title = "title",
    brands = "brands",
    name = "name",
    url = "url",
    openInNewTab = "openInNewTab",
    image = "image",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    fadeEffectWidth = "fadeEffectWidth",
    fadeEffectColor = "fadeEffectColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brands]: BrandProps[];
        [fields.maximumBrandImageHeight]: string;
        [fields.fadeEffectWidth]: number;
        [fields.fadeEffectColor]: string;
    };
}

interface BrandProps {
    fields: {
        [fields.name]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.image]: string;
    };
}

export interface FamilyOfBrandsStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    carouselWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    link?: LinkProps;
    image?: LazyImagePresentationProps;
    carouselExtendedStyles?: SwiperStyle;
}

type Props = OwnProps;

export const familyOfBrandsStyles: FamilyOfBrandsStyles = {};
const styles = familyOfBrandsStyles;

const FamilyOfBrands: React.FC<Props> = ({ fields, id }) => {
    const { title, brands, maximumBrandImageHeight, fadeEffectWidth, fadeEffectColor } = fields;

    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${maximumBrandImageHeight}px`,
        },
    };

    // TPS-484: TPS Family Of Brands Widget Bugs
    const renderBrands = [...brands, ...brands].map((brand, index) => {
        const { url, name, image, openInNewTab } = brand.fields;
        const history = useHistory();
        const link = useGetLink(url);
        const onClickHandler = (e: React.MouseEvent) => {
            e.preventDefault();
            if (!link.url) {
                return;
            }
            if (link.url.startsWith("http")) {
                if (openInNewTab) {
                    window.open(link.url);
                    return;
                }
                window.location.href = link.url;
            } else {
                if (openInNewTab) {
                    window.open(link.url);
                    return;
                }
                history.push(link.url);
            }
        };
        return (
            <StyledWrapper
                key={`${name}_${index}`}
                {...styles.imageWrapper}
                data-test-selector={`tps-family-logo_${name}`}
            >
                <Clickable onClick={onClickHandler} {...styles.link} aria-label={`${name}`} href={link.url}>
                    <LazyImage
                        imgProps={imgProps}
                        {...styles.image}
                        src={image}
                        altText={name}
                        placeholder={`${name} Brand`}
                    />
                </Clickable>
            </StyledWrapper>
        );
    });

    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE.mobile,
        [SLIDES_PER_VIEW_POINTS.sm]: FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE.tablet,
        [SLIDES_PER_VIEW_POINTS.md]: FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
        [SLIDES_PER_VIEW_POINTS.lg]: FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
    });

    const isTablet = isTabletScreen();
    let pagination: PaginationOptions | boolean = false;
    let navigation: NavigationOptions | boolean = false;
    let isScrollable = brands.length > FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop;
    if (isTablet) {
        pagination = {
            clickable: true,
        };
        navigation = true;
        isScrollable = false;
    }

    return (
        <StyledWrapper {...styles.container}>
            {title && <Typography {...styles.title}>{title}</Typography>}
            <StyledWrapper {...styles.carouselWrapper} {...{ isScrollable, fadeEffectWidth, fadeEffectColor }}>
                <XngCarousel
                    spaceBetween={0}
                    scrollbar={false}
                    navigation={navigation}
                    slidesPerView={FAMILY_OF_BRANDS_CAROUSEL_SLIDES_PER_SCREENSIZE.mobile}
                    breakpoints={breakpoints}
                    centerInsufficientSlides={false}
                    autoplay={{
                        delay: 2500,
                        disableOnInteraction: false,
                        stopOnLastSlide: false,
                    }}
                    pagination={pagination}
                    loop={true}
                    extendedStyles={styles.carouselExtendedStyles}
                >
                    {renderBrands}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Fade Effect Settings",
    sortOrder: 1,
};

export const definition: WidgetDefinition = {
    displayName: "TPS - Family of Brands",
    group: XngCustomGroup,
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: translate("Title"),
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
            tab: basicTab,
        },
        {
            name: fields.brands,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields, state) => {
                return item.fields.name;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: fields.name,
                    displayName: translate("Brand Name"),
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: fields.url,
                    displayName: translate("Brand Link"),
                    editorTemplate: "LinkField",
                    defaultValue: {
                        type: "Url",
                        value: "",
                    },
                },
                {
                    name: fields.openInNewTab,
                    displayName: "Open in New Tab",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                {
                    name: fields.image,
                    displayName: translate("Brand Logo"),
                    defaultValue: "",
                    editorTemplate: "ImagePickerField",
                    isRequired: true,
                },
            ],
            isRequired: true,
            tab: basicTab,
        },
        {
            name: fields.maximumBrandImageHeight,
            displayName: "Maximum brand image height",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            fieldType: "Contextual",
            tooltip:
                "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            validate: (value, item) => (value && value > 100 ? translate("Maximum logo height is 100px.") : null),
            tab: basicTab,
        },
        {
            name: fields.fadeEffectWidth,
            displayName: "Fade Effect Width in Pixels",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            fieldType: "Contextual",
            tab: advancedTab,
        },
        {
            name: fields.fadeEffectColor,
            displayName: "Fade Effect Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#0A57A4",
            fieldType: "Contextual",
            tab: advancedTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: FamilyOfBrands,
    definition,
};

export default widgetModule;
